import i18n from 'i18next'
import { hashString, prefersReducedMotion } from './pocketUnderline'

// The holder is a leather sleeve that never changes size. Each word is a card standing in it: the newest
// in front, each older card one step higher so its word shows above the card in front of it. On phones a
// holder floating over the story covers some of it: where the reading footer has room, the pocket becomes
// a plain footer button instead (WordPocket.tsx) and this view stays hidden; elsewhere it shrinks to a
// compact holder whose cards show only their top edges.
interface Geometry {
  sleeveW: number
  sleeveH: number
  radius: number
  /** How far each card behind stands above the one in front. */
  step: number
  /** How far the front card stands out of the sleeve. */
  peek: number
  /** Room above five cards for a card to lift. */
  liftRoom: number
  cardInset: number
  /** Size the flying characters shrink to as they land. */
  landFs: number
  compact: boolean
}
const DESKTOP: Geometry = {
  sleeveW: 96,
  sleeveH: 74,
  radius: 10,
  step: 17,
  peek: 19,
  liftRoom: 12,
  cardInset: 6,
  landFs: 13,
  compact: false,
}
const PHONE: Geometry = {
  sleeveW: 48,
  sleeveH: 36,
  radius: 8,
  step: 4,
  peek: 7,
  liftRoom: 2,
  cardInset: 5,
  landFs: 6,
  compact: true,
}
// Room around the sleeve for its shadow.
const PAD = 8
const CARD_TEXT_X = 8
const CARD_TEXT_Y = 2
const MAX_CARDS = 5

const isSmall = () => !!window.matchMedia?.('(max-width: 767px)').matches
const areaHeight = (g: Geometry) => g.liftRoom + g.peek + (MAX_CARDS - 1) * g.step + g.sleeveH + PAD
const sleeveTop = (g: Geometry) => areaHeight(g) - PAD - g.sleeveH
/** Top of a card, `depth` cards behind the front one. */
const cardTop = (g: Geometry, depth: number) => sleeveTop(g) - g.peek - depth * g.step

/** The sleeve's outline, inset by `i`. */
function sleevePath(w: number, h: number, radius: number, i: number) {
  const r = radius - i
  return (
    `M${i} ${i + r}A${r} ${r} 0 0 1 ${i + r} ${i}H${w - i - r}A${r} ${r} 0 0 1 ${w - i} ${i + r}` +
    `V${h - i - r}A${r} ${r} 0 0 1 ${w - i - r} ${h - i}H${i + r}A${r} ${r} 0 0 1 ${i} ${h - i - r}Z`
  )
}

/** Per-character boxes of an element's text, so each character can fly on its own. */
function charRects(el: Element) {
  const out: { ch: string; rect: DOMRect }[] = []
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  let node: Node | null
  while ((node = walker.nextNode())) {
    const text = node.textContent || ''
    let offset = 0
    for (const ch of Array.from(text)) {
      const range = document.createRange()
      range.setStart(node, offset)
      range.setEnd(node, offset + ch.length)
      offset += ch.length
      if (!ch.trim()) continue
      const rect = range.getBoundingClientRect()
      if (rect.width) out.push({ ch, rect })
    }
  }
  return out
}

export type ReleaseMode = 'remove' | 'mastered' | 'fail'

/**
 * The card holder in the bottom-left corner. Everything here is imperative because it is animation:
 * React only tells it which words are in the pocket.
 */
export class CardHolderView {
  private root: HTMLElement
  private inner: HTMLElement
  private sleeve: SVGSVGElement
  private cardsEl: HTMLElement
  private hit: HTMLElement
  private brand: HTMLElement
  private fx: HTMLElement
  private cards = new Map<string, HTMLElement>()
  private order: string[] = []
  private fresh = new Set<string>()
  private g: Geometry | null = null
  private active: string | null = null
  private docked = false
  /** The footer button's icon, which words fly into while the pocket is docked. */
  dockTarget: HTMLElement | null = null
  /** Turns the stored simplified word into what the learner reads (traditional, if they chose it). */
  labelFor: (word: string) => string = (word) => word
  /** Clicking a card opens the pocket on that word. */
  onCardClick: (word: string) => void = () => undefined

  constructor(root: HTMLElement, fx: HTMLElement) {
    this.root = root
    this.fx = fx
    this.inner = root.querySelector('.pk-inner') as HTMLElement
    this.sleeve = root.querySelector('.pk-sleeve') as SVGSVGElement
    this.cardsEl = root.querySelector('.pk-cards') as HTMLElement
    this.hit = root.querySelector('.pk-hit') as HTMLElement
    this.brand = root.querySelector('.pk-brand') as HTMLElement
    this.layout()
  }

  /** Draws the sleeve for this screen size. It only changes when the window crosses the phone breakpoint. */
  private layout() {
    const g = isSmall() ? PHONE : DESKTOP
    if (this.g === g) return
    this.g = g
    const { sleeveW: w, sleeveH: h } = g
    const W = w + PAD * 2
    const H = areaHeight(g)
    const top = sleeveTop(g)
    Object.assign(this.root.style, { width: `${W}px`, height: `${H}px` })
    this.root.classList.toggle('pk-compact', g.compact)
    document.documentElement.style.setProperty('--pk-w', `${W}px`)
    Object.assign(this.sleeve.style, { left: `${PAD}px`, top: `${top}px` })
    this.sleeve.setAttribute('width', String(w))
    this.sleeve.setAttribute('height', String(h))
    this.sleeve.setAttribute('viewBox', `0 0 ${w} ${h}`)
    this.sleeve.innerHTML = `
      <defs>
        <linearGradient id="pkSleeveShade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#fff" stop-opacity=".13"/>
          <stop offset=".38" stop-color="#fff" stop-opacity="0"/>
          <stop offset="1" stop-color="#000" stop-opacity=".16"/>
        </linearGradient>
      </defs>
      <path d="${sleevePath(w, h, g.radius, 0)}" fill="#068466"/>
      <path d="${sleevePath(w, h, g.radius, 0)}" fill="url(#pkSleeveShade)"/>
      <path d="M${g.radius} .6H${w - g.radius}" stroke="#fff" stroke-opacity=".32" stroke-width="1.2" stroke-linecap="round"/>
      <path d="${sleevePath(w, h, g.radius, 4)}" fill="none" stroke="#000" stroke-opacity=".14" stroke-width="1" stroke-dasharray="2.6 2.2" stroke-linecap="round" transform="translate(0 .7)"/>
      <path d="${sleevePath(w, h, g.radius, 4)}" fill="none" stroke="#fff" stroke-opacity=".42" stroke-width="1" stroke-dasharray="2.6 2.2" stroke-linecap="round"/>`
    // Cards are hidden below the sleeve's bottom edge.
    this.cardsEl.style.clipPath = `inset(-${H}px -${W}px ${PAD + 1}px -${W}px)`
    // Compact cards are too small to tap one by one, so the whole holder is the target there.
    const hitTop = g.compact ? cardTop(g, MAX_CARDS - 1) : top
    Object.assign(this.hit.style, {
      left: `${PAD}px`,
      top: `${hitTop}px`,
      width: `${w}px`,
      height: `${top + h - hitTop}px`,
    })
    // The wordmark is pressed into the lower half of the sleeve, as on a leather card holder.
    const brandW = Math.min(42, w * 0.62)
    Object.assign(this.brand.style, {
      left: `${PAD + w / 2}px`,
      top: `${top + h * 0.6}px`,
      width: `${brandW}px`,
      height: `${(brandW * 13) / 42}px`,
    })
    this.cards.forEach((el) => this.sizeCard(el))
    this.positionCards(this.order)
  }

  private sizeCard(el: HTMLElement) {
    const g = this.g as Geometry
    const face = el.querySelector('.pk-card-face') as HTMLElement
    face.style.width = `${g.sleeveW - g.cardInset * 2}px`
    face.style.height = `${areaHeight(g)}px`
  }

  private positionCards(order: string[]) {
    const g = this.g as Geometry
    order.forEach((word, i) => {
      const el = this.cards.get(word)
      if (!el || el.dataset.leaving) return
      const depth = order.length - 1 - i
      el.style.transform = `translate(${PAD + g.cardInset}px, ${cardTop(g, depth)}px)`
      el.style.zIndex = String(i + 1)
    })
    // The hover rules lift every card behind the hovered one, so the DOM must run back to front too.
    // Only re-append when it doesn't: moving a node cuts its running transition.
    const inDom = Array.from(this.cardsEl.children).filter((c) => !(c as HTMLElement).dataset.leaving)
    const wanted = order.map((w) => this.cards.get(w)).filter((c): c is HTMLElement => !!c && !c.dataset.leaving)
    if (inDom.some((c, i) => c !== wanted[i])) wanted.forEach((c) => this.cardsEl.appendChild(c))
  }

  private makeCard(word: string, order: string[]) {
    const el = document.createElement('span')
    el.className = 'pk-card'
    el.setAttribute('role', 'button')
    el.setAttribute('tabindex', '0')
    el.setAttribute('aria-label', i18n.t('reader:pocket.cardLabel', { word: this.labelFor(word) }))
    el.innerHTML =
      '<span class="pk-card-lift"><span class="pk-card-face"><span class="pk-card-word"></span></span></span>'
    ;(el.querySelector('.pk-card-word') as HTMLElement).textContent = this.labelFor(word)
    el.addEventListener('click', () => this.onCardClick(word))
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.onCardClick(word)
      }
    })
    this.sizeCard(el)
    // Placed before it can transition, so it never slides in from the corner.
    el.style.transition = 'none'
    this.cardsEl.appendChild(el)
    this.cards.set(word, el)
    this.positionCards(order)
    void el.offsetWidth
    el.style.transition = ''
    return el
  }

  /** Brings the cards in line with the pocket's words. */
  sync(words: string[]) {
    this.layout()
    this.order = words.slice()
    this.cards.forEach((el, word) => {
      if (words.indexOf(word) < 0 && !el.dataset.leaving) {
        el.remove()
        this.cards.delete(word)
      }
    })
    words.forEach((word) => {
      const existing = this.cards.get(word)
      if (existing) {
        ;(existing.querySelector('.pk-card-word') as HTMLElement).textContent = this.labelFor(word)
        return
      }
      const el = this.makeCard(word, words)
      // A card that arrived without a flight (another tab, a failed take-out) slides down into the sleeve.
      if (this.fresh.delete(word) && !prefersReducedMotion()) {
        ;(el.querySelector('.pk-card-lift') as HTMLElement).animate(
          [
            { transform: 'translateY(-22px)', opacity: 0 },
            { transform: 'none', opacity: 1 },
          ],
          { duration: 300, easing: 'cubic-bezier(.2,.8,.3,1)' },
        )
      }
    })
    this.positionCards(words)
    this.setActive(this.active)
    if (words.length) this.show()
    else this.hide()
  }

  /** Keeps the card whose word is open in the pocket lifted out of the sleeve. */
  setActive(word: string | null) {
    this.active = word
    this.cards.forEach((el, w) => el.classList.toggle('pk-card-active', w === word))
  }

  relayout() {
    this.layout()
  }

  /** While the pocket is a footer button, this holder keeps its cards in step but stays out of sight. */
  setDocked(docked: boolean) {
    this.docked = docked
    this.root.classList.toggle('pk-docked', docked)
  }

  markFresh(word: string) {
    this.fresh.add(word)
  }

  private show() {
    if (!this.root.classList.contains('pk-gone')) return
    this.inner.getAnimations().forEach((a) => a.cancel())
    this.root.classList.remove('pk-gone')
    if (prefersReducedMotion()) return
    this.inner.animate(
      [
        { transform: 'translateY(18px)', opacity: 0 },
        { transform: 'none', opacity: 1 },
      ],
      { duration: 280, easing: 'cubic-bezier(.2,.8,.3,1)' },
    )
  }

  private hide() {
    if (this.root.classList.contains('pk-gone')) return
    const done = () => {
      if (!this.order.length) this.root.classList.add('pk-gone')
    }
    if (prefersReducedMotion()) return done()
    this.inner.animate(
      [
        { transform: 'none', opacity: 1 },
        { transform: 'translateY(18px)', opacity: 0 },
      ],
      { duration: 200, easing: 'ease-in', fill: 'forwards' },
    ).onfinish = (e) => {
      done()
      ;(e.target as Animation | null)?.cancel()
    }
  }

  /** A card pushed home: the sleeve gives a little. */
  private settle() {
    if (prefersReducedMotion()) return
    this.inner.animate(
      [{ transform: 'none' }, { transform: 'translateY(1.5px)', offset: 0.35 }, { transform: 'none' }],
      { duration: 220, easing: 'ease-out' },
    )
  }

  /** The footer button takes a word in: its icon gives a little. */
  private bump(el: HTMLElement) {
    el.animate([{ transform: 'none' }, { transform: 'scale(1.22)', offset: 0.4 }, { transform: 'none' }], {
      duration: 300,
      easing: 'cubic-bezier(.3,1.4,.5,1)',
    })
  }

  /**
   * Lifts each character of `origin` and throws it in an arc onto a new front card, which forms under
   * them as they land (or into the footer button's icon while docked). Resolves when the last one lands.
   * Never waits on the network.
   */
  fly(word: string, origin: Element | null, wordsAfter: string[]): Promise<void> {
    return new Promise((resolve) => {
      this.layout()
      const g = this.g as Geometry
      this.order = wordsAfter.slice()
      this.show()
      // The cards already in the holder step back to make room in front.
      this.positionCards(wordsAfter)
      const card = this.cards.get(word) || this.makeCard(word, wordsAfter)
      const face = card.querySelector('.pk-card-face') as HTMLElement
      const chars = origin ? charRects(origin) : []
      if (prefersReducedMotion() || !chars.length) {
        setTimeout(resolve, 120)
        return
      }
      const dock = this.docked ? this.dockTarget : null
      const d = dock?.getBoundingClientRect()
      const r = this.root.getBoundingClientRect()
      const fs = g.landFs
      // Onto the card's word; onto the middle of a compact card's top edge, where no word shows; or into
      // the footer button's icon.
      let x0: number
      let y0: number
      if (d && d.width) {
        x0 = d.left + d.width / 2 - (chars.length * fs) / 2
        y0 = d.top + d.height / 2
      } else {
        face.style.opacity = '0'
        x0 = g.compact
          ? r.left + PAD + g.sleeveW / 2 - (chars.length * fs) / 2
          : r.left + PAD + g.cardInset + 1 + CARD_TEXT_X
        y0 = r.top + cardTop(g, 0) + 1 + (g.compact ? 0 : CARD_TEXT_Y + fs * 0.6)
      }
      const style = getComputedStyle(origin as Element)
      const fontSize = parseFloat(style.fontSize) || 40
      const endScale = fs / fontSize
      let landed = 0
      ;(origin as HTMLElement).classList.add('pk-ghost')
      chars.forEach(({ ch, rect }, i) => {
        const f = document.createElement('div')
        f.className = 'pk-flyer'
        f.textContent = ch
        Object.assign(f.style, {
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          lineHeight: `${rect.height}px`,
          fontFamily: style.fontFamily,
          fontSize: style.fontSize,
          fontWeight: style.fontWeight,
        })
        this.fx.appendChild(f)
        // Each character lands on its own place in the card's word.
        const dx = x0 + (i + 0.5) * fs - (rect.left + rect.width / 2)
        const dy = y0 - (rect.top + rect.height / 2)
        const lift = -14
        const ctrl = [dx * 0.3, Math.min(lift, dy) - 120]
        const rot = (i % 2 ? 1 : -1) * (4 + (hashString(word + i) % 6))
        const frames: Keyframe[] = [
          { transform: 'translate(0px,0px) scale(1) rotate(0deg)', offset: 0, easing: 'cubic-bezier(.2,.7,.3,1)' },
          { transform: `translate(0px,${lift}px) scale(1.1) rotate(${rot * 0.2}deg)`, offset: 0.14 },
        ]
        const N = 16
        for (let s = 1; s <= N; s++) {
          const t = s / N
          const u = 0.4 * t + 0.6 * t * t
          const a = 1 - u
          const x = 2 * a * u * ctrl[0] + u * u * dx
          const y = a * a * lift + 2 * a * u * ctrl[1] + u * u * dy
          const sc = 1.1 + (endScale - 1.1) * u
          frames.push({
            transform: `translate(${x.toFixed(1)}px,${y.toFixed(1)}px) scale(${sc.toFixed(3)}) rotate(${(rot * (1 - u) * 0.8).toFixed(1)}deg)`,
            offset: 0.14 + 0.86 * t,
          })
        }
        const anim = f.animate(frames, { duration: 620, delay: i * 55, easing: 'linear', fill: 'both' })
        anim.onfinish = () => {
          landed++
          if (landed === 1 && face.style.opacity === '0') {
            // The card forms under the first character to land.
            face.style.opacity = ''
            face.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' })
          }
          f.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, fill: 'forwards' }).onfinish = () => f.remove()
          if (landed === chars.length) {
            if (dock && d?.width) this.bump(dock)
            else this.settle()
            resolve()
            setTimeout(() => (origin as HTMLElement).classList.remove('pk-ghost'), 160)
          }
        }
      })
    })
  }

  /** Takes a card out: drawn up and away for Remove, raised green for Mastered, shaken loose for a failed save. */
  release(word: string, mode: ReleaseMode): Promise<void> {
    const el = this.cards.get(word)
    this.order = this.order.filter((w) => w !== word)
    if (!el) return Promise.resolve()
    el.dataset.leaving = '1'
    // The cards behind sink one step at once, while this one leaves on its own.
    this.positionCards(this.order)
    const finish = () => {
      el.remove()
      this.cards.delete(word)
    }
    if (prefersReducedMotion() || this.docked) {
      finish()
      return Promise.resolve()
    }
    const lift = el.querySelector('.pk-card-lift') as HTMLElement
    const out = -(this.g as Geometry).sleeveH
    let frames: Keyframe[]
    let options: KeyframeAnimationOptions
    if (mode === 'mastered') {
      el.classList.add('pk-card-mastered')
      frames = [
        { transform: 'none', opacity: 1 },
        { transform: 'translateY(-14px)', opacity: 1, offset: 0.4 },
        { transform: `translateY(${out}px)`, opacity: 0 },
      ]
      options = { duration: 620, easing: 'cubic-bezier(.3,.7,.3,1)', fill: 'forwards' }
    } else if (mode === 'fail') {
      frames = [
        { transform: 'none' },
        { transform: 'translateX(-3px) rotate(-3deg)', offset: 0.15 },
        { transform: 'translateX(3px) rotate(3deg)', offset: 0.3 },
        { transform: 'translateX(-2px)', offset: 0.45 },
        { transform: `translateY(${out}px)`, opacity: 0 },
      ]
      options = { duration: 600, easing: 'ease-in-out', fill: 'forwards' }
    } else {
      frames = [
        { transform: 'none', opacity: 1 },
        { transform: `translateY(${out}px)`, opacity: 0 },
      ]
      options = { duration: 300, easing: 'cubic-bezier(.5,0,.7,.4)', fill: 'forwards' }
    }
    return new Promise((resolve) => {
      lift.animate(frames, options).onfinish = () => {
        finish()
        resolve()
      }
    })
  }
}
